/**
 * WAVE 2a — RESTRAINED SUN GLARE (Sol ONLY). A small, occlusion-tested additive
 * diffraction sprite parked in Sol's screen direction. It fades with view angle
 * (invisible unless you look toward the sun) and with occlusion (a body between
 * camera and Sol snuffs it), so the register stays Cassini-photograph, never JJ
 * Abrams. Peak additive contribution is kept below the bloom threshold (1.0), so
 * it never blooms and the no-bloom-on-the-memorial rule is untouched — the glare
 * is welded to the sun direction and can never appear on any other light.
 *
 * The math (view falloff, sphere occlusion, opacity) is pure and unit-tested in
 * sunGlare.test.ts; the class only owns the sprite/texture plumbing.
 */
import * as THREE from 'three';

/** Peak sprite opacity — deliberately soft (additive, well under bloom threshold). */
export const GLARE_MAX_OPACITY = 0.5;
const GLARE_VIEW_MIN = 0.35; // Sol this aligned with view forward → glare starts fading in
const GLARE_VIEW_MAX = 0.96; // Sol nearly dead-centre → full glare
const GLARE_DISTANCE_KM = 40; // render-space distance to park the sprite from the camera
const GLARE_SPRITE_KM = 14; // on-screen sprite size (kept modest = restrained)

const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));
const clampSigned = (x: number): number => Math.min(1, Math.max(-1, x));

const smoothstep = (edge0: number, edge1: number, x: number): number => {
  if (edge0 === edge1) return x < edge0 ? 0 : 1;
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
};

/** Angular falloff: 0 when Sol is off-axis / behind, → 1 as it nears screen centre. Pure. */
export const glareViewFalloff = (viewDot: number): number =>
  smoothstep(GLARE_VIEW_MIN, GLARE_VIEW_MAX, viewDot);

/**
 * Sprite opacity from view alignment and occlusion. `viewDot` = camForward·sunDir
 * (both unit); `occlusion01` = 1 (Sol fully visible) .. 0 (fully blocked). Pure.
 */
export const glareOpacity = (viewDot: number, occlusion01: number): number =>
  GLARE_MAX_OPACITY * glareViewFalloff(viewDot) * clamp01(occlusion01);

/** A minimal 3-component vector shape (so the pure math needs no THREE import to test). */
export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

/**
 * How much a spherical body lets Sol through, seen from the camera along `dirToSun`
 * (unit). `camToCenter` is camera→body-centre (render space, km); `radiusKm` the
 * body radius. Returns 1 (Sol clear) .. 0 (Sol behind the disc). Pure.
 *
 * Only bodies IN FRONT of the camera toward the sun can occlude; a body behind the
 * camera, or angularly clear of the sun disc, returns 1.
 */
export const sphereOcclusion01 = (
  dirToSun: Vec3Like,
  camToCenter: Vec3Like,
  radiusKm: number,
): number => {
  const dist = Math.hypot(camToCenter.x, camToCenter.y, camToCenter.z);
  if (dist <= radiusKm) return 0; // camera inside/at the body → Sol fully hidden
  const along =
    camToCenter.x * dirToSun.x + camToCenter.y * dirToSun.y + camToCenter.z * dirToSun.z;
  if (along <= 0) return 1; // body is not between the camera and Sol
  const sep = Math.acos(clampSigned(along / dist)); // angle between Sol direction and body centre
  const bodyAng = Math.atan(radiusKm / dist); // angular radius of the body disc
  // Fully occluded inside the disc; soft edge to bodyAng*1.4 so the glare doesn't pop.
  return smoothstep(bodyAng, bodyAng * 1.4, sep);
};

/** Draw a soft radial core + faint diffraction spikes into a square canvas. */
const drawGlare = (ctx: CanvasRenderingContext2D, size: number): void => {
  const c = size / 2;
  const grad = ctx.createRadialGradient(c, c, 0, c, c, c);
  grad.addColorStop(0, 'rgba(255,255,255,1)');
  grad.addColorStop(0.25, 'rgba(220,238,246,0.55)');
  grad.addColorStop(1, 'rgba(220,238,246,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  // Two faint diffraction spikes (horizontal + vertical) — restrained, not a lens flare.
  ctx.strokeStyle = 'rgba(226,240,247,0.22)';
  ctx.lineWidth = Math.max(1, size / 96);
  ctx.beginPath();
  ctx.moveTo(0, c);
  ctx.lineTo(size, c);
  ctx.moveTo(c, 0);
  ctx.lineTo(c, size);
  ctx.stroke();
};

const makeGlareTexture = (): THREE.CanvasTexture => {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx) drawGlare(ctx, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
};

/** The Sol-only glare sprite. Construct once with the gameplay scene; update each frame. */
export class SunGlare {
  private readonly sprite: THREE.Sprite;
  private readonly forward = new THREE.Vector3();
  private readonly camPos = new THREE.Vector3();

  constructor(scene: THREE.Scene) {
    const mat = new THREE.SpriteMaterial({
      map: makeGlareTexture(),
      color: 0xcfe6ee, // cold Sol-white, in the sun palette
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthTest: false, // occlusion is analytic (sphereOcclusion01), not z-buffered
      depthWrite: false,
      toneMapped: false,
      opacity: 0,
    });
    this.sprite = new THREE.Sprite(mat);
    this.sprite.scale.setScalar(GLARE_SPRITE_KM);
    this.sprite.renderOrder = 999;
    this.sprite.visible = false;
    scene.add(this.sprite);
  }

  /**
   * Park the sprite in Sol's screen direction and set its opacity from view
   * alignment + occlusion. `sunDir` is the normalised render-space direction from
   * the camera toward Sol; `occlusion01` is 1 (clear) .. 0 (blocked by a body).
   */
  update(camera: THREE.Camera, sunDir: THREE.Vector3, occlusion01: number): void {
    camera.getWorldDirection(this.forward);
    const opacity = glareOpacity(this.forward.dot(sunDir), occlusion01);
    const mat = this.sprite.material as THREE.SpriteMaterial;
    mat.opacity = opacity;
    this.sprite.visible = opacity > 0.001;
    if (!this.sprite.visible) return;
    camera.getWorldPosition(this.camPos);
    this.sprite.position.set(
      this.camPos.x + sunDir.x * GLARE_DISTANCE_KM,
      this.camPos.y + sunDir.y * GLARE_DISTANCE_KM,
      this.camPos.z + sunDir.z * GLARE_DISTANCE_KM,
    );
  }
}
